import { useState } from "react";
import { FilterOption, FilterOptionType } from "../../filterPopover/FilterPopover.types.ts";
import { getResponsibleQueryUrl } from "../../../../user/hooks/useResponsibleQuery.ts";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { useDotLegalSnackbar, useStateSessionStorage } from "@dotlegal/dotlegal-ui-components";
import { AdvancedDocumentSearchResponse, AdvancedDocumentSortColumn } from "./AdvancedDocumentSearch.types.ts";
import { DocumentStatus } from "../../../../documents/Documents.types.ts";
import { useDotLegalMutation, useDotLegalPostGetQueryArray } from "@common/hooks/dotLegalReactQuery.ts";
import { DocumentEntityType } from "@common/components/search/SearchResultList.types.ts";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import { EntityType } from "@common/Common.types.ts";
import { useDotLegalFileUploadDataMapping } from "@common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks.ts";
import { post } from "@common/api/apiShared.ts";
import { selectableLegalEntitiesUrl } from "@common/hooks/useSelectableItemQueries.ts";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider.tsx";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { uiOperatorToWire } from "@common/components/filterBar/filterOperatorHydration.ts";

interface AdvancedDocumentSearchNonFilterState {
    searchString: string;
    pageNumber: number;
    sortingDirection: "asc" | "desc";
    sortColumn: AdvancedDocumentSortColumn;
}

function getDefaultNonFilterState(): AdvancedDocumentSearchNonFilterState {
    return {
        searchString: "",
        pageNumber: 1,
        sortingDirection: "asc",
        sortColumn: AdvancedDocumentSortColumn.DocumentTitle,
    };
}

export function useAdvancedDocumentSearch() {
    const snackbar = useDotLegalSnackbar();

    const { translateString } = useTranslation();
    const { downloadDocumentVersionByVersionId } = useDotLegalFileUploadDataMapping();
    const { customerId } = useUserContext();

    const [isDownloadingDocument, setIsDownloadingDocument] = useState("");

    const filterSessionKey = `advanced-document-search-${customerId}`;
    const { selectFilters, textFilters, booleanFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } =
        useFilterState(filterSessionKey);

    const [searchState, setSearchState] = useStateSessionStorage<AdvancedDocumentSearchNonFilterState>(
        `advanced-document-search-state-${customerId}`,
        getDefaultNonFilterState()
    );

    const hasFilterData = sessionStorage.getItem(filterSessionKey) !== null;
    const onlyCurrentVersion = hasFilterData ? !!booleanFilters["only-current-version"] : true;

    const url = "/documents/search/advanced";
    const requestBody = createSearchRequest();
    const queryKey = ["advancedDocumentSearch", requestBody];
    const { isLoading, data } = useDotLegalPostGetQueryArray<AdvancedDocumentSearchResponse>(queryKey, url, requestBody, {
        enabled: hasSearchCriteria(),
    });

    function hasSearchCriteria(): boolean {
        return !isNullOrWhitespace(searchState.searchString) || hasFilterValues();
    }

    function hasFilterValues(): boolean {
        return (
            !isNullOrWhitespace(textFilters.documentTitle) ||
            !isNullOrWhitespace(textFilters.entityTitle) ||
            (selectFilters.responsible ?? []).length > 0 ||
            (selectFilters["document-entity-types"] ?? []).length > 0 ||
            (selectFilters.statuses ?? []).length > 0 ||
            (selectFilters["document-type-ids"] ?? []).length > 0 ||
            (selectFilters["contract-parties"] ?? []).length > 0
        );
    }

    function createSearchRequest() {
        const documentEntityTypes = selectFilters["document-entity-types"] ?? [];
        const responsible = selectFilters.responsible ?? [];
        const statuses = selectFilters.statuses ?? [];
        const documentTypeIds = selectFilters["document-type-ids"] ?? [];
        const contractPartyIds = selectFilters["contract-parties"] ?? [];

        return {
            searchString: searchState.searchString || null,
            documentTitle: !isNullOrWhitespace(textFilters.documentTitle)
                ? { value: textFilters.documentTitle, filterOperator: uiOperatorToWire(filterOperators.documentTitle, FilterOptionType.TextField) }
                : null,
            entityTitle: !isNullOrWhitespace(textFilters.entityTitle)
                ? { value: textFilters.entityTitle, filterOperator: uiOperatorToWire(filterOperators.entityTitle, FilterOptionType.TextField) }
                : null,
            documentArea:
                documentEntityTypes.length > 0
                    ? {
                          value: documentEntityTypes.map((s) => parseInt(s)),
                          filterOperator: uiOperatorToWire(filterOperators["document-entity-types"], FilterOptionType.SelectField),
                      }
                    : null,
            responsible:
                responsible.length > 0
                    ? { value: responsible, filterOperator: uiOperatorToWire(filterOperators.responsible, FilterOptionType.SelectField) }
                    : null,
            status:
                statuses.length > 0
                    ? {
                          value: statuses.map((s) => parseInt(s)),
                          filterOperator: uiOperatorToWire(filterOperators.statuses, FilterOptionType.SelectField),
                      }
                    : null,
            documentTypeIds:
                documentTypeIds.length > 0
                    ? { value: documentTypeIds, filterOperator: uiOperatorToWire(filterOperators["document-type-ids"], FilterOptionType.SelectField) }
                    : null,
            contractPartyIds:
                contractPartyIds.length > 0
                    ? { value: contractPartyIds, filterOperator: uiOperatorToWire(filterOperators["contract-parties"], FilterOptionType.SelectField) }
                    : null,
            sortingDirection: searchState.sortingDirection,
            sortingColumn: searchState.sortColumn,
            page: searchState.pageNumber,
            onlyCurrentVersion,
        };
    }

    const onDownloadClick = async (
        documentId: string,
        documentVersionId: string,
        fileName: string,
        entityId: string,
        entityType: EntityType,
        link?: string
    ) => {
        if (!isNullOrWhitespace(fileName)) {
            setIsDownloadingDocument(documentVersionId);
            await downloadDocumentVersionByVersionId(documentId, documentVersionId, fileName, entityType, entityId);
            setIsDownloadingDocument("");
        } else {
            window.open(link, "_blank");
        }
    };

    const onTableSortingClick = (order: "asc" | "desc", sortingColumn: AdvancedDocumentSortColumn, pageNo: number) => {
        setSearchState({ ...searchState, sortingDirection: order, sortColumn: sortingColumn, pageNumber: pageNo });
    };

    const exportMutation = useDotLegalMutation(exportSearchApi);
    const onExportDocumentSearchClick = () => {
        exportMutation.mutateAsync(undefined, {
            onSuccess: () => {
                snackbar.show(translateString("fullDataExportInProgress"));
            },
        });
    };

    const onClearFilter = () => {
        onFilterChange([]);
        setSearchState(getDefaultNonFilterState());
    };

    const onSearchStringChange = (value: string) => {
        setSearchState({ ...searchState, searchString: value });
    };

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        filterSearch: { searchString: searchState.searchString, sortingDirection: searchState.sortingDirection },
        isLoading,
        data,
        mapSearchFilter: onFilterChange,
        getFilterDataOptions,
        hasSearchCriteria: hasSearchCriteria(),
        onDownloadClick,
        isDownloadingDocument,
        onTableSortingClick,
        onExportDocumentSearchClick,
        isExporting: exportMutation.isLoading,
        hasFilterValues: hasFilterValues(),
        onClearFilter,
        onSearchStringChange,
    };

    function getFilterDataOptions() {
        let filterOptions: Array<FilterOption> = [
            {
                id: "document-entity-types",
                label: translateString("documentArea"),
                type: FilterOptionType.SelectField,
                value: selectFilters["document-entity-types"] ?? [],
                fixedData: Object.values(DocumentEntityType)
                    .filter((value): value is DocumentEntityType => typeof value === "number")
                    .map((value) => ({
                        id: String(value),
                        name: translateString(setFirstLetterToLowerCase(DocumentEntityType[value].toString())),
                    })),
                filterOperator: filterOperators["document-entity-types"],
            },
            {
                id: "entityTitle",
                label: translateString("entity"),
                type: FilterOptionType.TextField,
                value: textFilters.entityTitle ?? "",
                filterOperator: filterOperators.entityTitle,
            },
            {
                id: "documentTitle",
                label: translateString("documentname"),
                type: FilterOptionType.TextField,
                value: textFilters.documentTitle ?? "",
                filterOperator: filterOperators.documentTitle,
            },
            {
                id: "statuses",
                label: translateString("status"),
                type: FilterOptionType.SelectField,
                value: selectFilters.statuses ?? [],
                fixedData: [
                    { id: DocumentStatus.active.toString(), name: translateString("active") },
                    { id: DocumentStatus.signed.toString(), name: translateString("signed") },
                    { id: DocumentStatus.draft.toString(), name: translateString("draft") },
                    { id: DocumentStatus.archived.toString(), name: translateString("archived") },
                ],
                filterOperator: filterOperators.statuses,
            },
            {
                id: "responsible",
                label: translateString("responsible"),
                type: FilterOptionType.SelectField,
                value: selectFilters.responsible ?? [],
                dataUrl: getResponsibleQueryUrl(),
                filterOperator: filterOperators.responsible,
            },
            {
                id: "document-type-ids",
                label: translateString("documentType"),
                type: FilterOptionType.SelectField,
                value: selectFilters["document-type-ids"] ?? [],
                dataUrl: `/documentTypes/all-selectable-items`,
                filterOperator: filterOperators["document-type-ids"],
            },
            {
                id: "contract-parties",
                label: translateString("contractParties"),
                type: FilterOptionType.SelectField,
                value: selectFilters["contract-parties"] ?? [],
                dataUrl: selectableLegalEntitiesUrl,
                filterOperator: filterOperators["contract-parties"],
            },
            {
                id: "only-current-version",
                label: translateString("version"),
                type: FilterOptionType.Boolean,
                value: onlyCurrentVersion ? "1" : "",
                fixedData: [{ id: "1", name: translateString("onlyCurrentVersion") }],
            },
        ];

        return filterOptions;
    }

    function exportSearchApi() {
        return post("/documents/search/export", createSearchRequest());
    }
}
